import { debounce, isMobileBreakpoint, mediaQueryLarge } from '@theme/utilities';

if(!customElements.get('scrollable-text')) {
  customElements.define('scrollable-text', class ScrollableText extends HTMLElement {
    constructor() {
      super();
      this.triggers = this.querySelectorAll('[data-scrolling-triger]');
      this.blocks = this.querySelectorAll('[data-scrolling-block]');
      this.progressBar = this.querySelector('[data-progress-bar]');
      this.blocksWrapper = this.querySelector('[data-groups]');
      this.isMobile = isMobileBreakpoint();
    }

    connectedCallback(){
      this.isMobile ? this.initMobile() : this.initDesktop();
      mediaQueryLarge.addEventListener("change", this.#onMediaQueryChange.bind(this));
    }

    disconnectedCallback(){
      mediaQueryLarge.removeEventListener("change", this.#onMediaQueryChange.bind(this));
    }

    /**
     * @param {MediaQueryListEvent} e
     */
    #onMediaQueryChange(e) {
      if(e.matches) {
        this.initDesktop()
      } else {
        this.initMobile();
        this.updateProgress();
      }
    }

    get sectionBehavior() {
      return this.dataset?.behavior;
    }

    initDesktop() {
      this.sectionBehavior === 'highlight_headings' ? this.initHighlightedHeadings() : this.setHighlightedBlock();
    }

    initMobile() {
      this.setActiveCategoryOnMobile();
      if(!this.blocksWrapper) return;
      document.addEventListener('DOMContentLoaded', ()=> this.updateProgress(this.blocksWrapper));
      this.blocksWrapper.addEventListener('scroll', ()=> this.updateProgress(this.blocksWrapper)); 
    }

    initHighlightedHeadings() {
      this.enableTriger();
      this.handleScroll();
      this.changeCategoriesOnScroll();
      this.changeCategoriesOnClick();
    }

    setActiveCategoryOnMobile() {
      if(this.blocks.length == 0) return;
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('active');
          } else {
             entry.target.classList.remove('active');
          }
        });
      }, {
        threshold: 0.6
      });

      this.blocks.forEach(block =>  observer.observe(block));
    }

    updateProgress() {
      const progress = ((this.blocksWrapper.scrollLeft + this.blocksWrapper.clientWidth) / this.blocksWrapper.scrollWidth) * 100;
      this.progressBar.style.cssText = `--progress-width: ${progress}%`;
    }

    enableTriger() {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.classList.add('active');
          } else {
            this.classList.remove('active');
          }
        });
      }, { threshold: 0.4 });

      observer.observe(this);
    }

    changeCategoriesOnScroll() {
      document.addEventListener('scroll', debounce(()=>{
        if(!this.classList.contains('active')) return;
        this.handleScroll();
      }, 100))
    }

    handleScroll() {
      if(!this.triggers.length === 0) return;
      this.blocks.forEach(block => {
        const parrentOffset = this.triggers[0]?.parentElement.getBoundingClientRect().top + 100;
        const blockOffsetTop = block.getBoundingClientRect().top;
        const blockOffsetBottom = block.getBoundingClientRect().bottom;
        if (parrentOffset >= blockOffsetTop && parrentOffset < blockOffsetBottom) {
          this.triggers.forEach(el => el.classList.remove('active'));
          const activeBlockTarget = block?.dataset?.scrollingBlock;
          const triger = this.querySelector(`[data-scrolling-triger="${activeBlockTarget}"]`);
          if(!triger) return;
          triger?.classList.add('active');
        }
      })
    }

    changeCategoriesOnClick() {
      if(this.triggers.length === 0) return;
        this.triggers.forEach(triger => {
          triger.addEventListener('click', ()=> {
            this.triggers.forEach(el => el.classList.remove('active'));
            triger.classList.add('active');
            const target = triger?.dataset?.scrollingTriger;
            const block = this.querySelector(`[data-scrolling-block="${target}"]`);
            if(!block) return;
            block.scrollIntoView(
              {
                block: 'center',
                behavior:'smooth'
              }
            )
          })
        })
    }

    setHighlightedBlock() {
      if (this.blocks.length == 0) return;
      let activeCounter = 0;
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('active');
              if (entry.intersectionRatio == 1) {
                activeCounter++;
              }
            } else {
              entry.target.classList.remove('active');
              if (entry.intersectionRatio >= 0.5 && entry.intersectionRatio < 1) {  
                activeCounter--;
              }
            }
          });
          const activeBlocks = this.querySelectorAll('[data-scrolling-block].active');
          if (activeCounter > 1) {
            activeBlocks.forEach((block) => block.classList.remove('highlighted'));
            activeBlocks[1]?.classList.add('highlighted');
          } else if (activeCounter <= 1) {
            activeBlocks.forEach((block) => block.classList.remove('highlighted'));
            activeBlocks[0]?.classList.add('highlighted');
          }
        },
        {
          threshold: 1,
        }
      );

      this.blocks.forEach((block) => observer.observe(block));
    }
  })
}